import { Suspense, useRef, useState, useEffect, memo } from 'react';
import { SECTION_COMPONENTS } from '@/lib/sectionRegistry';

export interface FallbackSection {
  sectionType: string;
  enabled: boolean;
}

interface DynamicPageRendererProps {
  fallbackSections: FallbackSection[];
  /** Optional CMS toggle overrides: maps sectionType → enabled boolean */
  sectionToggles?: Record<string, boolean | undefined>;
  /**
   * When true, CMS toggle data is still loading. Only the first (hero)
   * section will render eagerly; all other sections are held back until
   * CMS resolves, preventing hidden sections from briefly mounting.
   */
  cmsLoading?: boolean;
}

/**
 * Wrapper that defers mounting of below-fold sections until they are
 * near the viewport (400px margin). The first section (hero) renders
 * eagerly so there is zero delay above the fold.
 */
const DeferredSection = memo(({ sectionType, eager }: { sectionType: string; eager: boolean }) => {
  const Component = SECTION_COMPONENTS[sectionType];
  const ref = useRef<HTMLDivElement>(null);
  const [shouldMount, setShouldMount] = useState(eager);

  useEffect(() => {
    if (eager || shouldMount || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMount(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [eager, shouldMount]);

  if (!Component) {
    console.warn(`[DynamicPageRenderer] Unknown section type: ${sectionType}`);
    return null;
  }

  // For deferred sections, render a sentinel div first
  if (!shouldMount) {
    return <div ref={ref} style={{ minHeight: 1 }} />;
  }

  return (
    <div ref={!eager ? ref : undefined}>
      <Suspense fallback={null}>
        <Component />
      </Suspense>
    </div>
  );
});

DeferredSection.displayName = 'DeferredSection';

const DynamicPageRenderer = ({ fallbackSections, sectionToggles, cmsLoading }: DynamicPageRendererProps) => {
  const sections = fallbackSections.map(s => ({
    ...s,
    enabled: !cmsLoading && sectionToggles?.[s.sectionType] !== undefined
      ? sectionToggles[s.sectionType]!
      : s.enabled,
  }));

  const hero = sections[0];
  const remaining = sections.slice(1).filter(s => s.enabled);

  return (
    <>
      {/* Hero always mounted with stable key — never unmounts on CMS resolve */}
      {hero && hero.enabled && (
        <DeferredSection key={`hero-${hero.sectionType}`} sectionType={hero.sectionType} eager />
      )}

      {cmsLoading ? (
        <div style={{ minHeight: '100vh' }} />
      ) : (
        remaining.map((section, index) => (
          <DeferredSection
            key={`${section.sectionType}-${index}`}
            sectionType={section.sectionType}
            eager={false}
          />
        ))
      )}
    </>
  );
};

export default DynamicPageRenderer;
